import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/home";

export default function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(next, 5000);
    return () => clearInterval(interval);
  }, [isPaused, next]);

  return (
    <div
      className="relative w-full overflow-hidden bg-primary h-[420px] md:h-[600px]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Slides */}
      <div className="relative w-full h-full">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === current ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <div className="absolute inset-0">
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover object-center"
                width={1600}
                height={900}
                loading={index === 0 ? "eager" : "lazy"}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
            </div>
            <div className="relative w-full h-full flex items-center">
              <div className="w-full px-4 md:px-8 lg:px-16 pt-20 flex items-center">
                <div className="max-w-2xl">
                  <AnimatePresence mode="wait">
                    {index === current && (
                      <motion.div
                        key={slide.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <motion.h2
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: 0.1 }}
                          className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-5 text-white"
                        >
                          {slide.title}
                        </motion.h2>
                        <motion.p
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: 0.25 }}
                          className="text-sm md:text-base text-gray-200 leading-relaxed max-w-lg mb-8"
                        >
                          {slide.description}
                        </motion.p>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: 0.4 }}
                        >
                          <Link to="/solutions">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.97 }}
                              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white font-semibold rounded-md hover:bg-accent-light transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
                            >
                              Explore Solution
                              <i className="ri-arrow-right-line"></i>
                            </motion.button>
                          </Link>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Arrows */}
      <motion.button
        onClick={prev}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors cursor-pointer backdrop-blur-sm"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </motion.button>
      <motion.button
        onClick={next}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors cursor-pointer backdrop-blur-sm"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </motion.button>

      {/* Thumbnails */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-black/30 backdrop-blur-sm">
        <div className="flex items-center justify-center gap-2 px-4 py-3 overflow-x-auto hide-scrollbar">
          {heroSlides.map((slide, index) => (
            <motion.button
              key={slide.id}
              onClick={() => setCurrent(index)}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              className={`flex-shrink-0 cursor-pointer transition-all rounded overflow-hidden border-2 ${
                index === current
                  ? "border-accent opacity-100"
                  : "border-transparent opacity-60 hover:opacity-80"
              }`}
              aria-label={`Go to ${slide.title}`}
            >
              <img
                src={slide.thumbnail}
                alt={slide.title}
                loading="lazy"
                className="w-20 h-12 object-cover object-top"
                width={100}
                height={60}
              />
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}